/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  GamoraControllerContextIdentifier,
  GamoraFoundationGlueDefaultNamespace,
  GamoraFoundationGlueListenerType,
  GamoraFoundationNodeArgs,
  GamoraUUIDGuard,
  assert,
  debugCreate,
} from '@ver.id/libs.internal.gamora';
import {
  HoruzProtocolChulakServiceSessionGuardTagsDynamic,
  HoruzProtocolChulakServiceSessionGuardTagsType,
} from '../../guards/service-session.js';
import {
  HoruzProtocolChulakServiceStateInvoker,
  HoruzProtocolChulakServiceStateInvokerRefreshAppTagsInput,
  HoruzProtocolChulakServiceStateInvokerRefreshAppTagsResult,
  HoruzProtocolChulakServiceStateYielder,
} from './enums.js';
import { HoruzProtocolChulakDataType } from '../../data.js';
import { HoruzProtocolChulakLastState } from './../../states.js';
import { HoruzProtocolChulakMessageAgentStartCompletedGuard } from '../../guards/message/agent-start-completed.js';
import { HoruzProtocolChulakMessageAgentStartEnterGuard } from '../../guards/message/agent-start-enter.js';
import { HoruzProtocolChulakMessageAgentStartExitGuard } from '../../guards/message/agent-start-exit.js';
import { HoruzProtocolChulakMessageAppTagsRefreshedGuard } from '../../guards/message/app-tags-refreshed.js';
import { HoruzProtocolChulakMessageModule } from '../../modules/message.js';
import { HoruzProtocolChulakServiceStateBase } from './base.js';
import { HoruzProtocolChulakServiceStateContext } from '../context.js';
import { HoruzProtocolChulakServiceStateMap } from '../../statemap.js';
import { HoruzProtocolChulakTaskTagsInterval } from '../../tasks/index.js';
import { IHoruz } from '../../../../horuz.js';
const debug = debugCreate({
  namespace: 'horuz:protocols:chulak:states:service:agent-start',
});

const TAGS_INTERVAL_TASK_NAME = 'tagsInterval';

/**
 * Server
 *
 * AgentStart state
 * @public
 */
export class HoruzProtocolChulakServiceStateAgentStart extends HoruzProtocolChulakServiceStateBase<
  typeof HoruzProtocolChulakServiceStateMap,
  HoruzProtocolChulakServiceStateContext,
  IHoruz
> {
  constructor(args: GamoraFoundationNodeArgs) {
    super(args);
    this._initialize();
  }

  /**
   * Initialize default listeners
   */
  private _initialize() {
    this.$registerYield(
      GamoraFoundationGlueDefaultNamespace,
      HoruzProtocolChulakServiceStateYielder.PersistAgentStartMessage,
      this._onPersistAgentStartMessageYielder.bind(this),
    );

    this.$registerYield(
      GamoraFoundationGlueDefaultNamespace,
      HoruzProtocolChulakServiceStateYielder.BroadcastAgentStartToClients,
      this._onBroadcastAgentStartToClientsYielder.bind(this),
    );

    this.$registerYield(
      GamoraFoundationGlueDefaultNamespace,
      HoruzProtocolChulakServiceStateYielder.PersistAgentStartRecoveryFlag,
      this._onPersistAgentStartRecoveryFlagYielder.bind(this),
    );
  }

  /**
   * Event
   *
   * Persists agent start message to storage (single pod, with lock)
   * @param serviceSessionUuid The session uuid
   */
  private _onPersistAgentStartMessageYielder = async (
    serviceSessionUuid: GamoraUUIDGuard,
  ): Promise<void> => {
    return await this.serviceSessionStore.withLock(
      serviceSessionUuid,
      async () => {
        // Update service session
        const hasServiceSession =
          await this.serviceSessionStore.has(serviceSessionUuid);
        if (hasServiceSession) {
          // Get service session
          const serviceSession =
            await this.serviceSessionStore.get(serviceSessionUuid);

          // Get start message
          const startCompleted =
            HoruzProtocolChulakMessageAgentStartCompletedGuard.fromJSON({});

          // Set data to service session
          serviceSession.setData(
            HoruzProtocolChulakDataType.DATA_AGENT_START_COMPLETED,
            HoruzProtocolChulakMessageModule.Encode(startCompleted),
          );

          // Mark yielder as yielded
          serviceSession.addYielded(
            HoruzProtocolChulakServiceStateYielder.PersistAgentStartMessage,
          );

          // Store service session
          await this.serviceSessionStore.upsert(serviceSession);
        }
      },
    );
  };

  /**
   * Event
   *
   * Marks recovery flag for reconnecting clients (single pod via sync-session queue, with lock)
   * @param serviceSessionUuid The session uuid
   */
  private _onPersistAgentStartRecoveryFlagYielder = async (
    serviceSessionUuid: GamoraUUIDGuard,
  ): Promise<void> => {
    return await this.serviceSessionStore.withLock(
      serviceSessionUuid,
      async () => {
        const hasServiceSession =
          await this.serviceSessionStore.has(serviceSessionUuid);
        if (hasServiceSession) {
          const serviceSession =
            await this.serviceSessionStore.get(serviceSessionUuid);

          // Mark as yielded only if not already marked
          if (
            !serviceSession.hasYielded(
              HoruzProtocolChulakServiceStateYielder.BroadcastAgentStartToClients,
            )
          ) {
            serviceSession.addYielded(
              HoruzProtocolChulakServiceStateYielder.BroadcastAgentStartToClients,
            );
            await this.serviceSessionStore.upsert(serviceSession);
          }
        }
      },
    );
  };

  /**
   * Event
   *
   * Broadcasts agent start to local WebSocket clients (all pods via notify-session SNS, no lock)
   * @param serviceSessionUuid The session uuid
   */
  private _onBroadcastAgentStartToClientsYielder = async (
    serviceSessionUuid: GamoraUUIDGuard,
  ): Promise<void> => {
    const hasServiceSession =
      await this.serviceSessionStore.has(serviceSessionUuid);

    // No session, nothing to do
    if (!hasServiceSession) {
      return;
    }

    // Read session data WITHOUT lock (read-only operation)
    const serviceSession =
      await this.serviceSessionStore.get(serviceSessionUuid);

    // Get the startCompleted message to send to clients
    const startCompleted = serviceSession.getDataByType(
      HoruzProtocolChulakDataType.DATA_AGENT_START_COMPLETED,
    );

    // Check if THIS pod has any active WebSocket contexts for this session
    const myActiveContextIdentifiers: string[] = [];
    for (const serviceContextIdentifier of serviceSession.contextIdentifiers) {
      if (this.$controller.$hasContext(serviceContextIdentifier)) {
        myActiveContextIdentifiers.push(serviceContextIdentifier);
      }
    }

    // Send messages to OUR active WebSocket contexts (no lock needed)
    // Only pods with local contexts will send
    if (myActiveContextIdentifiers.length > 0) {
      for (const serviceContextIdentifier of myActiveContextIdentifiers) {
        const controllerContext = this.$controller.$getContext(
          serviceContextIdentifier,
        );
        const serviceStateContext = controllerContext.stateContext;

        this._trySendMessageAndTransition(
          serviceStateContext,
          'AppPairing',
          startCompleted,
        );
      }
    }
  };

  /**
   * Initialize interval task to ask for new tags
   * @param stateContext
   */
  private _startTagsIntervalTask = async (
    serviceStateContext: HoruzProtocolChulakServiceStateContext,
    settings: HoruzProtocolChulakServiceSessionGuardTagsDynamic,
  ) => {
    const taskManager = this.$root
      .$('protocols')
      .$('chulak')
      .$('tasks')
      .$('manager');

    assert(
      !taskManager.$hasTask(
        serviceStateContext.contextIdentifier,
        TAGS_INTERVAL_TASK_NAME,
      ),
      `Invalid state: task ${TAGS_INTERVAL_TASK_NAME} must not be running.`,
    );

    // Create authorization timer
    const timeoutTimerTask = new HoruzProtocolChulakTaskTagsInterval({
      namespace: serviceStateContext.contextIdentifier,
      name: TAGS_INTERVAL_TASK_NAME,
      callback: async () => {
        await this.$tryPostTask(serviceStateContext, async () => {
          if (this.$hasPostedTransition(serviceStateContext)) {
            return;
          }

          try {
            const tagsInput =
              await this.$executeInvocation<HoruzProtocolChulakServiceStateInvokerRefreshAppTagsResult>(
                serviceStateContext.contextIdentifier,
                HoruzProtocolChulakServiceStateInvoker.RefreshAppTags,
                serviceStateContext,
                {
                  serviceSessionUuid:
                    serviceStateContext.stateContextRegistry.serviceSessionUuid,
                },
              );

            // Execute invokation
            const tagsRefreshed =
              HoruzProtocolChulakMessageAppTagsRefreshedGuard.fromJSON(
                tagsInput,
              );

            // Send start message
            await this.send(serviceStateContext, tagsRefreshed);
          } catch (e: any) {
            debug.log('(_onAuthMessage): Unable to authenticate request. ');
            debug.trace(e?.message, e?.stack);
            this._tryHandleException(serviceStateContext, e);
          }
        });
      },
      ms: settings.frequency,
    });

    // Start task
    await timeoutTimerTask.$start();

    // Register task in manager
    taskManager.$registerTask(timeoutTimerTask);
  };

  /**
   * Stop interval task
   * @param stateContext
   */
  private _stopTagsIntervalTask = async (
    serviceStateContext: HoruzProtocolChulakServiceStateContext,
  ) => {
    const taskManager = this.$root
      .$('protocols')
      .$('chulak')
      .$('tasks')
      .$('manager');

    // Already stopped
    if (
      !taskManager.$hasTask(
        serviceStateContext.contextIdentifier,
        TAGS_INTERVAL_TASK_NAME,
      )
    ) {
      return;
    }

    const timeoutTimerTask = taskManager.$getTask(
      serviceStateContext.contextIdentifier,
      TAGS_INTERVAL_TASK_NAME,
    );

    // Stop and unregister task
    await timeoutTimerTask.$terminate();
    taskManager.$unregisterTask(timeoutTimerTask);
  };

  /**
   * Enter state
   */
  override async $enter(
    serviceStateContext: HoruzProtocolChulakServiceStateContext,
  ): Promise<void> {
    assert(
      serviceStateContext.stateContextRegistry.serviceSessionUuid,
      'Invalid argument: serviceSessionUuid not available in stateContextRegistry.',
    );
    try {
      return await this.serviceSessionStore.withLock(
        serviceStateContext.stateContextRegistry.serviceSessionUuid,
        async () => {
          try {
            assert(
              serviceStateContext.stateContextRegistry.serviceSessionUuid,
              'Invalid argument: serviceSessionUuid not available in stateContextRegistry.',
            );
            // Get session
            const serviceSession = await this.serviceSessionStore.get(
              serviceStateContext.stateContextRegistry.serviceSessionUuid,
            );

            // Send State message
            const startEnter =
              HoruzProtocolChulakMessageAgentStartEnterGuard.fromJSON({});

            // Send start message
            await this.send(serviceStateContext, startEnter);

            // Store
            serviceSession.lastState = HoruzProtocolChulakLastState.AgentStart;
            await this.serviceSessionStore.upsert(serviceSession);

            // Recovery
            if (
              serviceSession.hasYielded(
                HoruzProtocolChulakServiceStateYielder.BroadcastAgentStartToClients,
              )
            ) {
              const startCompleted = serviceSession.getDataByType(
                HoruzProtocolChulakDataType.DATA_AGENT_START_COMPLETED,
              );

              this._trySendMessageAndTransition(
                serviceStateContext,
                'AppPairing',
                startCompleted,
              );
            }

            switch (serviceSession.tags.type) {
              case HoruzProtocolChulakServiceSessionGuardTagsType.Dynamic:
                await this._startTagsIntervalTask(
                  serviceStateContext,
                  serviceSession.tags.dynamic,
                );
                break;
            }
          } catch (e: any) {
            debug.log('($enter): Unable to handle serviceSession. ');
            debug.trace(e?.message, e?.stack);
            this._tryHandleException(serviceStateContext, e);
          }
        },
      );
    } catch (e: any) {
      debug.log('($enter): Unable to get serviceSession lock. ');
      debug.trace(e?.message, e?.stack);
      this._tryHandleException(serviceStateContext, e);
    }
  }

  /**
   * Exit state
   */
  override async $exit(
    serviceStateContext: HoruzProtocolChulakServiceStateContext,
  ): Promise<void> {
    assert(
      serviceStateContext.stateContextRegistry.serviceSessionUuid,
      'Invalid argument: serviceSessionUuid not available in stateContextRegistry.',
    );
    try {
      await this._stopTagsIntervalTask(serviceStateContext);

      // Aborting or stopping are the next states
      const contextState = this.$controller.$getContext(
        serviceStateContext.contextIdentifier,
      );
      if (
        contextState.stateNext == 'Abort' ||
        contextState.stateNext == 'Stopped'
      ) {
        return;
      }

      // Finalize
      return await this.serviceSessionStore.withLock(
        serviceStateContext.stateContextRegistry.serviceSessionUuid,
        async () => {
          try {
            assert(
              serviceStateContext.stateContextRegistry.serviceSessionUuid,
              'Invalid argument: serviceSessionUuid not available in stateContextRegistry.',
            );

            // Send state
            const startExit =
              HoruzProtocolChulakMessageAgentStartExitGuard.fromJSON({});

            // Send message
            await this.send(serviceStateContext, startExit);
          } catch (e: any) {
            debug.log('($exit): Unable to handle serviceSession. ');
            debug.trace(e?.message, e?.stack);
            this._tryHandleException(serviceStateContext, e);
          }
        },
      );
    } catch (e: any) {
      debug.log('($exit): Unable to get serviceSession lock. ');
      debug.trace(e?.message, e?.stack);
      this._tryHandleException(serviceStateContext, e);
    }
  }

  /**
   * Register yielders
   * @public
   */
  yielder(
    serviceSessionUuid: GamoraUUIDGuard,
    name: typeof HoruzProtocolChulakServiceStateYielder.PersistAgentStartMessage,
  ): Promise<void>;
  yielder(
    serviceSessionUuid: GamoraUUIDGuard,
    name: typeof HoruzProtocolChulakServiceStateYielder.BroadcastAgentStartToClients,
  ): Promise<void>;
  yielder(
    serviceSessionUuid: GamoraUUIDGuard,
    name: typeof HoruzProtocolChulakServiceStateYielder.PersistAgentStartRecoveryFlag,
  ): Promise<void>;
  yielder<K extends keyof typeof HoruzProtocolChulakServiceStateYielder>(
    serviceSessionUuid: GamoraUUIDGuard,
    name: K,
    ...args: unknown[]
  ): Promise<void> {
    return super.$executeYielder(
      GamoraFoundationGlueDefaultNamespace,
      name,
      serviceSessionUuid,
      ...args,
    );
  }

  /**
   * Register invokers
   */
  invoker(
    contextIdentifier: GamoraControllerContextIdentifier,
    name: typeof HoruzProtocolChulakServiceStateInvoker.RefreshAppTags,
    listener: (
      stateContext: HoruzProtocolChulakServiceStateContext,
      input: HoruzProtocolChulakServiceStateInvokerRefreshAppTagsInput,
    ) => Promise<HoruzProtocolChulakServiceStateInvokerRefreshAppTagsResult>,
  ): this;
  invoker<K extends keyof typeof HoruzProtocolChulakServiceStateInvoker>(
    contextIdentifier: GamoraControllerContextIdentifier,
    name: K,
    listener: GamoraFoundationGlueListenerType,
  ): this {
    return super.$registerInvoker(contextIdentifier, name, listener);
  }
}
